import React, { useCallback, useMemo } from 'react'
import {
  generatePaginationItems,
  PaginationItem,
  sanitizePagination,
} from '../../../Helper/EnhancedData/PaginationUtils'

// Defines number of buttons be appeared on the page including ellipsis and anchors
const MAX_PAGE_BUTTONS = 8

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  visiblePages?: number;
}

const PaginationComponent: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  onPageChange,
  visiblePages = MAX_PAGE_BUTTONS,
}) => {
  const { current, total } = sanitizePagination(currentPage, totalPages)

  // Handle page changes and notify the parent component with callback
  const handlePageChange = useCallback((page: number): void => {
    const isActivePage = page === current
    if (!isActivePage) onPageChange(page)
  }, [onPageChange, current])

  const goPreviousPage = useCallback(() => {
    if (current > 1) {
      const prevPage = current - 1
      onPageChange(prevPage)
    }
  }, [onPageChange, current])

  const goNextPage = useCallback(() => {
    if (current < total) {
      const nextPage = current + 1
      onPageChange(nextPage)
    }
  }, [onPageChange, current, total])

  // Generate Pagination Items
  const maxVisibleButtons = Math.max(1, Math.floor(visiblePages))
  const pageItems = useMemo<PaginationItem[]>(
    () => generatePaginationItems(current, total, maxVisibleButtons), [current, total, maxVisibleButtons]
  )

  if (total === 0) return null

  const isFirstPage = current === 1
  const isLastPage = current === total
  const backwardPageBtn = (
    <button
      type="button"
      onClick={goPreviousPage}
      disabled={isFirstPage}
      aria-disabled={isFirstPage}
      className={`pagination-block-item pagination-nav-arrow ${isFirstPage ? 'disabled' : ''}`}
      aria-label="Previous page"
    >
      &lt;
    </button>
  )
  const forwardPageBtn = (
    <button
      type="button"
      onClick={goNextPage}
      disabled={isLastPage}
      aria-disabled={isLastPage}
      className={`pagination-block-item pagination-nav-arrow ${isLastPage ? 'disabled' : ''}`}
      aria-label="Next page"
    >
      &gt;
    </button>
  )
  return (
    <div className="flex justify-center" aria-label="Pagination">
      <div className="pagination-block-list">
        {backwardPageBtn}
        {/* Render page buttons with ellipses */}
        {pageItems.map((item, index) => {
          if (item === 'ellipsis') {
            return (
              <span
                key={`e-${index}`}
                className="pagination-block-ellipsis"
                aria-hidden
              >
                &hellip;
              </span>
            )
          }
          const itemIsActive = item === current
          const classNames = ['pagination-block-item', itemIsActive ? 'active' : ''].join(' ').trim()
          return (
            <button
              key={item}
              type="button"
              onClick={() => handlePageChange(item)}
              className={classNames}
              aria-current={itemIsActive ? 'page' : undefined}
              aria-label={itemIsActive ? `Page ${item}, current` : `Go to page ${item}`}
            >
              {item}
            </button>
          )
        })}
        {forwardPageBtn}
      </div>
    </div>
  )
}

export const Pagination = React.memo(PaginationComponent)
